import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { calculateResults, calculateMonthlyData, calculatePayrollMonthlyData } from '../utils/calculations';
import type { CalculationResults, MonthlyData } from '../types';

export interface MonthlyTotals {
  totalHours: number;
  workDays: number;
  holidays: number;
  vacationDays: number;
  sickDays: number;
}

export interface CalculationsData {
  results: CalculationResults;
  monthlyData: MonthlyData[];
  payrollMonthlyData: MonthlyData[];
  yearlyTotals: MonthlyTotals;
  payrollYearlyTotals: MonthlyTotals;
}

interface UseCalculationsOptions {
  employmentStart?: string | null;
  employmentEnd?: string | null;
}

export function useCalculations(options?: UseCalculationsOptions): CalculationsData {
  const { state, getCarryOverFromPreviousYear, getPreviousYearEntries } = useApp();
  const { entries, settings, year, settingsSegments } = state;
  const employmentStart = options?.employmentStart;
  const employmentEnd = options?.employmentEnd;

  return useMemo(() => {
    const carryOver = getCarryOverFromPreviousYear();
    // Lade Vorjahresdaten für korrekte 13-Wochen-Berechnung des Urlaubsentgelts
    const previousYearEntries = getPreviousYearEntries();
    const results = calculateResults(
      entries, settings, year, new Date(), carryOver, previousYearEntries, 0,
      employmentStart, employmentEnd, settingsSegments
    );
    const monthlyData = calculateMonthlyData(entries, year);
    const payrollMonthlyData = calculatePayrollMonthlyData(entries, year, previousYearEntries);

    const sumTotals = (data: MonthlyData[]): MonthlyTotals =>
      data.reduce(
        (acc, month) => ({
          totalHours: acc.totalHours + month.totalHours,
          workDays: acc.workDays + month.workDays,
          holidays: acc.holidays + month.holidays,
          vacationDays: acc.vacationDays + month.vacationDays,
          sickDays: acc.sickDays + month.sickDays,
        }),
        { totalHours: 0, workDays: 0, holidays: 0, vacationDays: 0, sickDays: 0 }
      );

    const yearlyTotals = sumTotals(monthlyData);
    const payrollYearlyTotals = sumTotals(payrollMonthlyData);

    return { results, monthlyData, payrollMonthlyData, yearlyTotals, payrollYearlyTotals };
  }, [entries, settings, year, settingsSegments, getCarryOverFromPreviousYear, getPreviousYearEntries, employmentStart, employmentEnd]);
}
